<template>
    <v-container fluid class="pa-5 overflow-auto">
        <h4>{{ $t('metadata_for', { title: record.title }) }}</h4>
        <table class="stripe-table mt-2 tgk-paragraph-text">
            <tbody>
                <tr>
                    <td>{{ $t('rec_id') }}</td>
                    <td>
                        <span>{{ record.id }}</span> / UUID: <span>{{ record.uuid }}</span>
                    </td>
                </tr>
                <tr>
                    <td>{{ $t('created') }}</td>
                    <td>
                        {{ record.created.formatIf('dd, DD.MM.YYYY HH:mm') }}
                    </td>
                </tr>
                <tr>
                    <td>{{ $t('creator') }}</td>
                    <td>
                        {{ record.creator }}
                    </td>
                </tr>
                <tr>
                    <td>{{ $t('modified') }}</td>
                    <td>
                        {{ record.modified.formatIf('dd, DD.MM.YYYY HH:mm') }}
                        <span v-if="record.modifier"> / {{ record.modifier }}</span>
                    </td>
                </tr>
                <tr v-if="legalInfo">
                    <td>Legal</td>
                    <td>
                        <dl v-for="info in legalInfo" :key="info.acceptanceType" class="legal-entry">
                            <dt>
                                <KpIcon icon-cls="tick" color="grey" left size="16" /> {{ $t(info.acceptanceType) }}
                            </dt>
                            <dd>{{ info.user }}, {{ info.date.formatIf('DD.MM.YYYY HH:MM') }}</dd>
                        </dl>
                    </td>
                </tr>
                <tr>
                    <td>{{ $t('hauptsparte') }}</td>
                    <td>
                        {{ record.main_genre_group }}
                    </td>
                </tr>
                <tr>
                    <td>{{ $t('weitere_sparten') }}</td>
                    <td>
                        {{ record.event_genre_groups }}
                    </td>
                </tr>
                <tr>
                    <td>{{ $t('import_protection') }}</td>
                    <td>
                        {{ record.import_protection ? $t('yes') : $t('no') }}
                    </td>
                </tr>
                <tr>
                    <td>{{ $t('is_foreign_import') }}</td>
                    <td>
                        {{ record.is_foreign_import ? $t('yes') : $t('no') }}
                    </td>
                </tr>
                <tr>
                    <td>Business Partner's Original ID</td>
                    <td>
                        {{ record.bp_import_id }}
                    </td>
                </tr>
                <tr>
                    <td>Business Partner Identification</td>
                    <td>
                        {{ record.bp_import_origin }}
                    </td>
                </tr>
                <tr>
                    <td>Import Date</td>
                    <td>
                        {{ record.bp_import_date.formatIf('dd, DD.MM.YYYY HH:mm') }}
                    </td>
                </tr>
                <tr>
                    <td>GUID</td>
                    <td>
                        {{ record.uuid || '-' }}
                    </td>
                </tr>
                <tr v-if="!hideFrontendLinks">
                    <td>{{ $t('link') }}</td>
                    <td>
                        <a :href="record.detailUrl" target="_blank">{{ record.detailUrl }}</a>
                    </td>
                </tr>
                <tr v-if="!hideFrontendLinks">
                    <td>{{ $t('qr_code') }}</td>
                    <td>
                        <a
                            :href="apiUrl(`/event/linkQrCode?id=${record.id}&type=png&force_download=true`)"
                            target="_blank"
                        >
                            <img
                                :src="apiUrl(`/event/linkQrCode?id=${record.id}&type=svg`)"
                                width="150"
                                height="150"
                                alt="QR Code"
                            />
                        </a>
                    </td>
                </tr>
            </tbody>
        </table>
    </v-container>
</template>

<script>
import { Event } from '@/models/Event';
import { apiUrl, globalParam } from '@/helpers/globals';
import { instance as api } from '@/helpers/api';
import dayjs from 'dayjs';

export default {
    name: 'EventMetadata',
    props: {
        record: {
            type: Event,
            required: true,
        },
    },
    data() {
        return {
            additionalMetadata: null,
        };
    },
    computed: {
        additionalMetadataAsJson() {
            return JSON.stringify(this.additionalMetadata, undefined, 4);
        },
        legalInfo() {
            if (this.additionalMetadata?.legalAcceptances?.length) {
                return this.additionalMetadata.legalAcceptances.map((entry) => {
                    return {
                        user: entry.user,
                        acceptanceType: entry.acceptanceType,
                        date: dayjs(entry.timestamp),
                    };
                });
            } else {
                return null;
            }
        },
        hideFrontendLinks() {
            return globalParam('event.hide_frontend_links');
        },
    },
    created() {
        this.apiUrl = apiUrl;
        this.loadAdditionalMetadata();
    },
    methods: {
        async loadAdditionalMetadata() {
            try {
                const ret = await api().get('/event/loadMetadata', {
                    params: { id: this.record.id },
                });
                if (ret?.data) {
                    this.additionalMetadata = ret.data;
                }
            } catch (e) {
                // pass
            }
        },
    },
};
</script>

<style scoped lang="scss">
.legal-entry {
    display: flex;
    gap: 5px;
    dt {
        flex-basis: 150px;
    }
    dd {
        flex: 1;
    }
}
</style>
